import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Inbox, Loader2, Mail, Check, CheckCheck, Download } from "lucide-react";
import { Button } from "@/components/ui/button";

// Bulk CSV export — pipes through the browser so tickets can feed any CRM
// without needing an integration. Two flavours so admins can either grab
// everything for archiving or just the current filtered view for handover.
const csvSafe = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
const HEADER = ["id", "created_at", "name", "email", "subject", "message", "ip", "resolved", "updated_at"];
const downloadCsv = (rows, filename) => {
  const lines = [HEADER.join(",")].concat(
    rows.map((r) => HEADER.map((k) => csvSafe(r[k])).join(","))
  );
  const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
};

/**
 * Super-admin only. Contact-form submissions from the /contact page — same
 * messages that get emailed to the owner, but with a resolved-toggle so the
 * team can work through them without hunting through the inbox.
 */
export default function ContactInboxTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("open"); // open | resolved | all

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/contact-messages");
      setRows(data.rows || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const toggle = async (m) => {
    try {
      await api.patch(`/admin/contact-messages/${m.id}`, { resolved: !m.resolved });
      toast.success(m.resolved ? "Reopened" : "Marked resolved");
      load();
    } catch (e) { toast.error("Update failed"); }
  };

  const filtered = rows.filter((m) => {
    if (filter === "open") return !m.resolved;
    if (filter === "resolved") return !!m.resolved;
    return true;
  });

  const stamp = new Date().toISOString().slice(0, 10);
  const exportAll = () => {
    if (!rows.length) return toast.error("Nothing to export");
    downloadCsv(rows, `contact-messages-all-${stamp}.csv`);
    toast.success(`Exported ${rows.length} messages`);
  };
  const exportFiltered = () => {
    if (!filtered.length) return toast.error("No messages in current view");
    downloadCsv(filtered, `contact-messages-${filter}-${stamp}.csv`);
    toast.success(`Exported ${filtered.length} messages (${filter})`);
  };

  return (
    <div data-testid="contact-inbox-tab">
      <div className="flex items-start justify-between mb-4 flex-wrap gap-3">
        <div>
          <div className="text-caption">Support</div>
          <h1 className="font-display text-3xl font-extrabold">Contact Inbox</h1>
          <p className="text-slate-400 text-sm mt-1">Messages submitted via /contact land here so nothing falls through the cracks.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button size="sm" variant="outline" onClick={exportFiltered} className="bg-transparent border-white/10 h-8" data-testid="inbox-export-filtered">
            <Download className="w-3 h-3 mr-1.5" /> Export {filter}
          </Button>
          <Button size="sm" onClick={exportAll} className="btn-gold h-8" data-testid="inbox-export-all">
            <Download className="w-3 h-3 mr-1.5" /> Export all CSV
          </Button>
        </div>
      </div>
      <div className="flex items-center justify-end mb-4">
        <div className="flex gap-1 bg-black/20 border border-white/10 rounded-full p-1" data-testid="inbox-filter-group">
          {["open", "resolved", "all"].map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-3 py-1 rounded-full text-xs font-mono uppercase tracking-wider transition-colors ${
                filter === f ? "bg-red-500 text-white" : "text-slate-400 hover:text-white"
              }`}
              data-testid={`inbox-filter-${f}`}
            >
              {f}{f !== "all" && ` (${rows.filter((r) => (f === "open") !== !!r.resolved).length})`}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="glass-card rounded-2xl p-8 flex items-center gap-2 text-slate-400"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="glass-card gold-border rounded-2xl p-10 text-center" data-testid="inbox-empty">
          <Inbox className="w-8 h-8 mx-auto text-red-400/60 mb-2" />
          <p className="text-slate-400">{filter === "open" ? "Inbox zero — nothing waiting." : "Nothing here."}</p>
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map((m) => (
            <div key={m.id} className={`glass-card rounded-xl p-4 border ${m.resolved ? "border-emerald-400/20 opacity-70" : "gold-border"}`} data-testid={`inbox-row-${m.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <span className="font-display font-bold text-white">{m.subject}</span>
                    {m.resolved && (
                      <span className="text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded bg-emerald-500/10 border border-emerald-400/30 text-emerald-300">Resolved</span>
                    )}
                  </div>
                  <div className="text-xs text-slate-400 mb-2 font-mono">
                    <b className="text-slate-200">{m.name}</b> · <a href={`mailto:${m.email}`} className="text-red-300 hover:text-red-200">{m.email}</a> · {new Date(m.created_at).toLocaleString()} · IP {m.ip}
                  </div>
                  <p className="text-sm text-slate-300 leading-relaxed whitespace-pre-wrap">{m.message}</p>
                </div>
                <div className="flex flex-col gap-2 shrink-0">
                  <Button size="sm" variant="outline" onClick={() => toggle(m)} className={`bg-transparent h-8 ${m.resolved ? "border-white/10 text-slate-300" : "border-emerald-400/40 text-emerald-300 hover:bg-emerald-400/10"}`} data-testid={`inbox-toggle-${m.id}`}>
                    {m.resolved ? <><Check className="w-3 h-3 mr-1" /> Reopen</> : <><CheckCheck className="w-3 h-3 mr-1" /> Resolve</>}
                  </Button>
                  <a href={`mailto:${m.email}?subject=Re: ${encodeURIComponent(m.subject)}`} className="inline-flex items-center justify-center gap-1 rounded-md h-8 px-3 text-xs font-medium bg-red-500/10 border border-red-400/30 text-red-300 hover:bg-red-500/20" data-testid={`inbox-reply-${m.id}`}>
                    <Mail className="w-3 h-3" /> Reply
                  </a>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
